import { MoreHoriz, TransferWithinAStation } from '@mui/icons-material';
import { Grid } from '@mui/material';
import { dateFormat } from '@nx-smartmonkey/shared/helpers';
import { HSpacer, RoutalAvatar, RoutalLoader } from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Check, Close } from '@mui/icons-material';
import RegistrationIcon from '../../../../../assets/img/backoffice/created_at.svg';
import EmailIcon from '../../../../../assets/img/backoffice/email.svg';
import LanguageIcon from '../../../../../assets/img/backoffice/language.svg';
import PhoneIcon from '../../../../../assets/img/backoffice/phone.svg';
import * as routing from '../../../../../constants/Routing';
import { ProjectBase } from '../../../../../domain/backoffice/project/ProjectBase';
import { intl } from '../../../../../helpers/IntGlobalProvider';
import { useRetrieveUser } from '../../../../../hooks/backoffice/users/useRetrieveUser';
import { useAsUser } from '../../../../../hooks/session/useAsUser';
import { useRetrieveAdminUser } from '../../../../../hooks/user/useRetrieveAdminUser';
import { useRetrieveUser as useRetrieveLoggedUser } from '../../../../../hooks/user/useRetrieveUser';
import Button from '../../../../../react-components/Button';
import Text from '../../../../../react-components/Text';
import { DataTable } from '../../../../../react-components/data-table/DataTable';
import { getUploadAsBase64 } from '../../../../../services/requests';
import { InfoBox, InfoBoxItem, InfoBoxText } from '../../../shared/InfoBox';
import { SystemsInfoBox } from '../../../shared/SystemsInfoBox';
import { getProjectsTableColumns } from '../../../shared/detailsProjectsTableColumns';
import { BackofficeClientsUserActionsMenu } from '../shared/BackofficeClientsUserActionsMenu';

export const BackofficeClientsUsersDetails = () => {
  const { beAsUser } = useAsUser();
  const { data: adminUser } = useRetrieveAdminUser();
  const { data: loggedUser } = useRetrieveLoggedUser();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: user, isLoading, error, run: refetchUser } = useRetrieveUser({ userId });
  const [hideDeletedProjects, setHideDeletedProjects] = useState(false);

  useEffect(() => {
    if (error) {
      navigate(routing.BACKOFFICE.CLIENTS.USERS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  if (isLoading) {
    return (
      <Grid
        item
        container
        direction="column"
        style={{ height: `100%`, width: `100%` }}
        alignItems="center"
        justifyContent="center"
      >
        <RoutalLoader />
      </Grid>
    );
  }

  if (!user) return null;

  const visibleProjects = !hideDeletedProjects
    ? user.projects
    : user.projects.filter((project: ProjectBase) => !project.deleted);

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{ backgroundColor: `white`, zIndex: 2, height: `100%`, overflowY: `auto` }}
    >
      <Grid
        style={{ padding: `16px 22px`, borderBottom: `1px solid #e0e0e0` }}
        container
        item
        justifyContent="space-between"
        direction="row"
        wrap="nowrap"
      >
        <Grid item container direction="row" alignItems="center">
          <RoutalAvatar
            large
            url={user.avatar_url}
            getUploadAsBase64={getUploadAsBase64}
            label={`${user.first_name} ${user.last_name}`}
          />

          <HSpacer medium />

          <Text variant="body-large">{`${user.first_name} ${user.last_name}`},</Text>

          <HSpacer small />

          <Text
            variant="button"
            onClick={(e: any) => {
              navigate(`${routing.BACKOFFICE.CLIENTS.ORGANIZATIONS}/details/${user.organization.id}`);
            }}
          >
            {user.organization.label}
          </Text>
        </Grid>

        <Grid item container direction="row" alignItems="center" justifyContent="flex-end">
          {user.id !== adminUser?.id && user.id !== loggedUser?.id ? (
            <>
              <Button
                styleType="secondary"
                onClick={() => {
                  beAsUser({ id: user.id });
                  window.open(routing.PLANNER.ROOT, `_blank`);
                }}
                style={{
                  padding: `6px 10px`,
                }}
              >
                <TransferWithinAStation />
              </Button>

              <HSpacer small />
            </>
          ) : null}

          <BackofficeClientsUserActionsMenu
            user={user}
            type="popover"
            popoverProps={{
              hideArrow: true,
              content: <MoreHoriz />,
              containerStyle: {
                width: `unset`,
              },
            }}
            refreshAfterUpdate={() => refetchUser()}
          />
        </Grid>
      </Grid>

      <div style={{ padding: `16px 22px`, overflow: `auto` }}>
        <Grid container direction="column" spacing={2}>
          <Grid item style={{ maxWidth: `500px` }}>
            <SystemsInfoBox email={user.email} subscription={user.organization.subscription} />
          </Grid>

          <Grid item style={{ maxWidth: `500px` }}>
            <InfoBox title={intl.formatMessage({ id: `backoffice.information` })}>
              <div style={{ height: `calc(100% - 32px)`, width: `calc(100% - 16px)`, padding: `0 8px` }}>
                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.users.email` })}
                  icon={<img src={EmailIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{user.email}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.users.phone` })}
                  icon={<img src={PhoneIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{user.phone}</InfoBoxText>
                </InfoBoxItem>

                {/* TODO Role, Plans Created, Stops Created, Optimizations */}

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.users.language` })}
                  icon={<img src={LanguageIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText intlId={`settings.account.profile.${user.language.value}`} />
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.users.created_at` })}
                  icon={<img src={RegistrationIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                >
                  <InfoBoxText>{dateFormat(user.created_at)}</InfoBoxText>
                </InfoBoxItem>

                <InfoBoxItem
                  label={intl.formatMessage({ id: `backoffice.clients.users.validation` })}
                  icon={<img src={RegistrationIcon} style={{ width: `18px` }} alt="" draggable="false" />}
                  childrenItemProps={{
                    style: {
                      justifyContent: `center`,
                      alignItems: `center`,
                      display: `flex`,
                    },
                  }}
                >
                  {user.validation ? <Check style={{ color: `#70D292` }} /> : <Close style={{ color: `#E03030` }} />}
                </InfoBoxItem>
              </div>
            </InfoBox>
          </Grid>

          <Grid item style={{ maxWidth: `1500px` }}>
            <InfoBox
              title={intl.formatMessage({ id: `backoffice.clients.projects_num` }, { num: visibleProjects.length })}
              showText={intl.formatMessage({ id: `backoffice.shared.info_box.show` })}
              hideText={intl.formatMessage({ id: `backoffice.shared.info_box.hide` })}
              hide={hideDeletedProjects}
              onHide={(status) => setHideDeletedProjects(status)}
            >
              <DataTable
                loading={isLoading}
                showCheckboxes={false}
                docs={visibleProjects.map((project: ProjectBase) => ({ doc: project }))}
                noFooter
                hover
                total={visibleProjects.length}
                columns={getProjectsTableColumns({
                  defaultProjectId: user.default_project_id,
                  navigate: (destinationUrl: string) => navigate(destinationUrl),
                  onRefreshContent: () => refetchUser(),
                })}
              />
            </InfoBox>
          </Grid>
        </Grid>
      </div>
    </Grid>
  );
};
